<div [formGroup]="form" class="flex flex-col gap-6">
  @if (!hideStepHeader()) {
    <div class="flex flex-col gap-3">
      <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h2 class="text-3xl font-extrabold text-neutral-12">
          <button
            type="button"
            class="text-left font-inherit text-3xl font-extrabold text-neutral-12 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-9 rounded cursor-pointer"
            (click)="debugForm('Characteristics')"
            (keydown.enter)="debugForm('Characteristics')"
            (keydown.space)="debugForm('Characteristics'); $event.preventDefault()"
          >
            Précisez les caractéristiques de votre
            {{ propertyDefinition().propertyType | etxeEnumDisplay: 'propertyType' | lowercase }}
          </button>
        </h2>
        @if (isSkippable()) {
          <button
            mat-flat-button
            (click)="skipped.emit()"
            type="button"
            class="bg-neutral-3 text-neutral-12 hover:bg-neutral-4"
          >
            <mat-icon fontIcon="skip_next"></mat-icon>
            Passer cette étape
          </button>
        }
      </div>
      @if (isSkippable()) {
        <div class="flex items-start gap-2 rounded-2xl bg-blue-2 p-4 text-blue-11">
          <mat-icon fontIcon="info" class="shrink-0 text-xl"></mat-icon>
          <p class="text-sm">
            Cette étape est longue car elle détaille tout le potentiel de votre bien. Bien que facultative,
            elle est essentielle pour rassurer les acheteurs et valoriser votre annonce. Vous pourrez y
            revenir à tout moment.
          </p>
        </div>
      }
    </div>
  }

  <!-- Surfaces Counter / Indicator (Non-blocking guidance) -->
  @if (targetGroundSurface() || (isHouse() && targetLandSurface())) {
    <div class="rounded-2xl border border-neutral-6 bg-neutral-1 p-4 shadow-xs">
      <div class="flex gap-4 flex-col">
        <!-- Info Title -->
        <div class="flex items-center gap-3">
          <div
            class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-3 text-primary"
          >
            <mat-icon fontIcon="straighten" class="text-xl"></mat-icon>
          </div>
          <div>
            <div class="flex items-center gap-2">
              <h4 class="text-sm font-bold text-neutral-12">Suivi des superficies</h4>
              <span class="rounded-full bg-neutral-3 px-2 py-0.5 text-[10px] font-medium text-neutral-11">
                Aide facultative
              </span>
            </div>
            <p class="text-xs text-neutral-11">
              Visualisez les m² renseignés par rapport aux informations clés de votre bien
            </p>
          </div>
        </div>

        <!-- Metric Cards -->
        <div class="flex flex-wrap items-center gap-3 justify-between">
          <!-- Land Surface Card (pour les maisons) -->
          @if (isHouse() && targetLandSurface(); as targetLand) {
            <div
              class="flex min-w-[200px] flex-1 flex-col gap-1 rounded-xl border border-neutral-5 bg-neutral-2 p-2.5 sm:flex-initial"
              matTooltip="Superficie totale de la parcelle cadastrale (titre de propriété)"
              matTooltipPosition="above"
            >
              <div class="flex items-center justify-between text-xs">
                <span class="font-medium text-neutral-11">Superficie du terrain</span>
                <span class="font-semibold text-neutral-12">{{ targetLand }} m²</span>
              </div>
              <div class="text-[11px] text-neutral-11">
                @if (totalExteriorSurface() > 0) {
                  <span>Extérieurs renseignés : {{ totalExteriorSurface() }} m²</span>
                } @else {
                  <span>Aucun extérieur renseigné</span>
                }
              </div>
            </div>
          }

          <!-- Habitable Surface reference if distinct from Ground -->
          @if (targetHabitableSurface(); as targetHab) {
            @if (propertyKeyInfo()?.surface?.ground && targetGroundSurface() !== targetHab) {
              <div
                class="flex min-w-[190px] flex-1 flex-col gap-1 rounded-xl border border-neutral-5 bg-neutral-2 p-2.5 sm:flex-initial"
                matTooltip="Surface habitable déclarée (hauteur sous plafond ≥ 1,80 m)"
                matTooltipPosition="above"
              >
                <div class="flex items-center justify-between text-xs">
                  <span class="font-medium text-neutral-11">Surface habitable</span>
                  <span class="font-semibold text-neutral-12">{{ targetHab }} m²</span>
                </div>
                <div class="text-[11px] text-neutral-11">
                  @if (targetGroundSurface()) {
                    <span>Dont {{ targetGroundSurface()! - targetHab }} m² sous 1,80 m</span>
                  }
                </div>
              </div>
            }
          }

          <!-- Ground Surface Card (Surface au sol) -->
          @if (targetGroundSurface(); as targetGround) {
            <div
              class="flex min-w-[210px] flex-1 flex-col gap-1 rounded-xl border p-2.5 transition-colors sm:flex-initial"
              [class.border-jade-6]="groundSurfaceStatus() === 'complete'"
              [class.bg-jade-2]="groundSurfaceStatus() === 'complete'"
              [class.border-orange-5]="groundSurfaceStatus() === 'missing'"
              [class.bg-orange-2]="groundSurfaceStatus() === 'missing'"
              [class.border-ruby-5]="groundSurfaceStatus() === 'exceeded'"
              [class.bg-ruby-2]="groundSurfaceStatus() === 'exceeded'"
              matTooltip="Superficie au sol totale : ensemble des espaces intérieurs renseignés par rapport à la superficie au sol déclarée (tolérance ±1%)"
              matTooltipPosition="above"
            >
              <div class="flex items-center justify-between text-xs">
                <span class="font-medium text-neutral-11">Surface au sol</span>
                <span
                  class="font-semibold"
                  [class.text-jade-11]="groundSurfaceStatus() === 'complete'"
                  [class.text-orange-11]="groundSurfaceStatus() === 'missing'"
                  [class.text-ruby-11]="groundSurfaceStatus() === 'exceeded'"
                >
                  {{ totalInteriorSurface() }} / {{ targetGround }} m²
                </span>
              </div>
              <div class="h-1.5 w-full overflow-hidden rounded-full bg-neutral-4">
                <div
                  class="h-1.5 rounded-full transition-all duration-300"
                  [class.bg-jade-9]="groundSurfaceStatus() === 'complete'"
                  [class.bg-orange-9]="groundSurfaceStatus() === 'missing'"
                  [class.bg-ruby-9]="groundSurfaceStatus() === 'exceeded'"
                  [style.width.%]="groundProgressPercent()"
                ></div>
              </div>
              <div class="flex items-center justify-between text-[11px]">
                @if (groundSurfaceStatus() === 'complete') {
                  <span class="inline-flex items-center gap-1 font-medium text-jade-11">
                    <mat-icon fontIcon="check_circle" class="text-xs"></mat-icon>
                    Conforme (±1%)
                  </span>
                } @else if (groundSurfaceStatus() === 'missing') {
                  <span class="font-medium text-orange-11">
                    Reste {{ remainingGroundSurface() }} m² à ventiler
                  </span>
                } @else {
                  <span class="inline-flex items-center gap-1 font-medium text-ruby-11">
                    <mat-icon fontIcon="warning" class="text-xs"></mat-icon>
                    +{{ groundSurfaceOverflow() }} m² au-dessus du déclaré
                  </span>
                }
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  }

  <div class="flex flex-col gap-6">
    <!-- Interior spaces -->
    <div class="rounded-3xl border border-orange-3 bg-orange-1 shadow-sm">
      <button
        type="button"
        (click)="toggleSection('interior')"
        class="flex w-full cursor-pointer items-center justify-between p-6 text-left outline-none md:p-10"
      >
        <div class="flex items-center gap-4">
          <div class="flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-3 text-orange">
            <mat-icon fontIcon="meeting_room" class="text-2xl"></mat-icon>
          </div>
          <div>
            <div class="flex items-center gap-2">
              <h3 class="text-xl font-bold text-neutral-12">Espaces intérieurs</h3>
              <mat-icon
                class="text-xs! h-4! w-4! text-neutral-10 cursor-help"
                matTooltip="Pièces intérieures fermées (salons, chambres, cuisines, salles d'eau). Leur somme est comparée à la superficie au sol déclarée."
                matTooltipPosition="above"
                (click)="$event.stopPropagation()"
              >
                help_outline
              </mat-icon>
              @if (targetGroundSurface(); as targetGround) {
                <span
                  class="rounded-full px-2.5 py-0.5 text-xs font-semibold"
                  [class.bg-jade-3]="groundSurfaceStatus() === 'complete'"
                  [class.text-jade-11]="groundSurfaceStatus() === 'complete'"
                  [class.bg-orange-3]="groundSurfaceStatus() === 'missing'"
                  [class.text-orange-11]="groundSurfaceStatus() === 'missing'"
                  [class.bg-ruby-3]="groundSurfaceStatus() === 'exceeded'"
                  [class.text-ruby-11]="groundSurfaceStatus() === 'exceeded'"
                >
                  {{ totalInteriorSurface() }} / {{ targetGround }} m²
                  @if (groundSurfaceStatus() === 'missing') {
                    (reste {{ remainingGroundSurface() }} m²)
                  } @else if (groundSurfaceStatus() === 'complete') {
                    (conforme)
                  } @else if (groundSurfaceStatus() === 'exceeded') {
                    (+{{ groundSurfaceOverflow() }} m² au-dessus)
                  }
                </span>
              } @else if (totalInteriorSurface() > 0) {
                <span class="rounded-full bg-orange-3 px-2.5 py-0.5 text-xs font-semibold text-orange-11">
                  {{ totalInteriorSurface() }} m²
                </span>
              }
            </div>
            <p class="text-sm text-neutral-11">Détails des pièces et de la configuration</p>
          </div>
        </div>
        <mat-icon>{{ expandedInterior() ? 'expand_less' : 'expand_more' }}</mat-icon>
      </button>

      @if (expandedInterior()) {
        <div class="px-6 pb-10 md:px-10">
          <div class="flex flex-col gap-8">
            <div class="rounded-2xl border border-orange-3 bg-orange-a2 p-6">
              <etxe-property-spaces-list
                title="Pièces supplémentaires (Salon, Bureau, etc.)"
                [formArray]="form.controls.interior.controls.rooms"
                itemLabel="Pièce"
                addButtonLabel="Ajouter une pièce"
                [typeOptions]="roomTypes"
                typeEnumLabel="room"
                (add)="addSpace('interior.rooms')"
                (remove)="removeSpace('interior.rooms', $event)"
              ></etxe-property-spaces-list>
            </div>

            <div class="rounded-2xl border border-orange-3 bg-orange-a2 p-6">
              <etxe-property-spaces-list
                title="Chambres"
                [formArray]="form.controls.interior.controls.bedroom"
                itemLabel="Chambre"
                addButtonLabel="Ajouter une chambre"
                hint="Minimum 9m² avec fenêtre"
                descriptionPlaceholder="Ex: Suite parentale, placard..."
                (add)="addSpace('interior.bedroom')"
                (remove)="removeSpace('interior.bedroom', $event)"
              ></etxe-property-spaces-list>
            </div>

            <div class="rounded-2xl border border-orange-3 bg-orange-a2 p-6">
              <etxe-property-spaces-list
                title="Salles de bain"
                [formArray]="form.controls.interior.controls.bathroom"
                itemLabel="Salle de bain"
                addButtonLabel="Ajouter"
                hint="Avec baignoire"
                descriptionPlaceholder="Ex: Double vasque..."
                (add)="addSpace('interior.bathroom')"
                (remove)="removeSpace('interior.bathroom', $event)"
              ></etxe-property-spaces-list>
            </div>
            <div class="rounded-2xl border border-orange-3 bg-orange-a2 p-6">
              <etxe-property-spaces-list
                title="Salles d’eau"
                [formArray]="form.controls.interior.controls.showerRoom"
                itemLabel="Salle d'eau"
                addButtonLabel="Ajouter"
                hint="Avec douche"
                descriptionPlaceholder="Ex: Douche à l'italienne..."
                (add)="addSpace('interior.showerRoom')"
                (remove)="removeSpace('interior.showerRoom', $event)"
              ></etxe-property-spaces-list>
            </div>
            <div class="rounded-2xl border border-orange-3 bg-orange-a2 p-6">
              <div class="w-full max-w-sm">
                <aura-number-input
                  label="Nombre de toilettes WC"
                  [value]="form.controls.interior.controls.wc.value ?? undefined"
                  (valueChange)="form.controls.interior.controls.wc.setValue($event ?? null)"
                  [min]="0"
                  description="Nombre total de coins toilettes"
                ></aura-number-input>
              </div>
            </div>
          </div>
        </div>
      }
    </div>

    <!-- Outer spaces & Exterior -->
    <div class="rounded-3xl border border-jade-3 bg-jade-2 shadow-sm">
      <button
        type="button"
        (click)="toggleSection('exterior')"
        class="flex w-full cursor-pointer items-center justify-between p-6 text-left outline-none md:p-10"
      >
        <div class="flex items-center gap-4">
          <div class="flex h-12 w-12 items-center justify-center rounded-2xl bg-jade-5 text-primary">
            <mat-icon fontIcon="deck" class="text-2xl"></mat-icon>
          </div>
          <div>
            <div class="flex items-center gap-2">
              <h3 class="text-xl font-bold text-neutral-12">Espaces extérieurs</h3>
              <mat-icon
                class="text-xs! h-4! w-4! text-neutral-10 cursor-help"
                matTooltip="Espaces extérieurs privatifs (balcons, terrasses, loggias, jardins, piscines, caves). Ces surfaces s'ajoutent à la surface habitable."
                matTooltipPosition="above"
                (click)="$event.stopPropagation()"
              >
                help_outline
              </mat-icon>
              @if (totalExteriorSurface() > 0) {
                <span class="rounded-full bg-jade-3 px-2.5 py-0.5 text-xs font-semibold text-jade-11">
                  {{ totalExteriorSurface() }} m² renseignés
                </span>
              }
            </div>
            <p class="text-sm text-neutral-11">Terrasses, jardins et dépendances vous appartenant</p>
          </div>
        </div>
        <mat-icon>{{ expandedExterior() ? 'expand_less' : 'expand_more' }}</mat-icon>
      </button>

      @if (expandedExterior()) {
        <div class="px-6 pb-10 md:px-10">
          <div class="grid grid-cols-1 gap-4">
            <div class="rounded-2xl border border-jade-3 bg-jade-a2 p-6">
              <etxe-property-spaces-list
                title="Loggias"
                [formArray]="form.controls.exterior.controls.loggia"
                itemLabel="Loggia"
                addButtonLabel="Ajouter"
                [showLotNumber]="propertyIsCoOwnership()"
                (add)="addSpace('exterior.loggia')"
                (remove)="removeSpace('exterior.loggia', $event)"
              ></etxe-property-spaces-list>
            </div>

            <div class="rounded-2xl border border-jade-3 bg-jade-a2 p-6">
              <etxe-property-spaces-list
                title="Terrasses"
                [formArray]="form.controls.exterior.controls.terrace"
                itemLabel="Terrasse"
                addButtonLabel="Ajouter"
                [showLotNumber]="propertyIsCoOwnership()"
                (add)="addSpace('exterior.terrace')"
                (remove)="removeSpace('exterior.terrace', $event)"
              ></etxe-property-spaces-list>
            </div>

            <div class="rounded-2xl border border-jade-3 bg-jade-a2 p-6">
              <etxe-property-spaces-list
                title="Balcons"
                [formArray]="form.controls.exterior.controls.balcony"
                itemLabel="Balcon"
                addButtonLabel="Ajouter"
                [showLotNumber]="propertyIsCoOwnership()"
                (add)="addSpace('exterior.balcony')"
                (remove)="removeSpace('exterior.balcony', $event)"
              ></etxe-property-spaces-list>
            </div>

            <div class="rounded-2xl border border-jade-3 bg-jade-a2 p-6">
              <etxe-property-spaces-list
                title="Jardins"
                [formArray]="form.controls.exterior.controls.garden"
                itemLabel="Jardin"
                addButtonLabel="Ajouter"
                [showLotNumber]="propertyIsCoOwnership()"
                (add)="addSpace('exterior.garden')"
                (remove)="removeSpace('exterior.garden', $event)"
              ></etxe-property-spaces-list>
            </div>

            <div class="rounded-2xl border border-jade-3 bg-jade-a2 p-6">
              <etxe-property-spaces-list
                title="Piscines"
                [formArray]="form.controls.exterior.controls.swimmingPool"
                itemLabel="Piscine"
                addButtonLabel="Ajouter"
                [showLotNumber]="propertyIsCoOwnership()"
                (add)="addSpace('exterior.swimmingPool')"
                (remove)="removeSpace('exterior.swimmingPool', $event)"
              ></etxe-property-spaces-list>
            </div>

            <div class="rounded-2xl border border-jade-3 bg-jade-a2 p-6">
              <etxe-property-spaces-list
                title="Caves"
                [formArray]="form.controls.exterior.controls.cellar"
                itemLabel="Cave"
                addButtonLabel="Ajouter"
                [showLotNumber]="propertyIsCoOwnership()"
                (add)="addSpace('exterior.cellar')"
                (remove)="removeSpace('exterior.cellar', $event)"
              ></etxe-property-spaces-list>
            </div>
          </div>
        </div>
      }
    </div>

    <!-- Parking Section -->
    <div class="rounded-3xl border border-blue-3 bg-blue-2 shadow-sm">
      <button
        type="button"
        (click)="toggleSection('parking')"
        class="flex w-full cursor-pointer items-center justify-between p-6 text-left outline-none md:p-10"
      >
        <div class="flex items-center gap-4">
          <div class="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-5 text-primary">
            <mat-icon fontIcon="local_parking" class="text-2xl"></mat-icon>
          </div>
          <div>
            <h3 class="text-xl font-bold text-neutral-12">Parking et stationnement</h3>
            <p class="text-sm text-neutral-11">Indiquez les options de stationnement disponibles</p>
          </div>
        </div>
        <mat-icon>{{ expandedParking() ? 'expand_less' : 'expand_more' }}</mat-icon>
      </button>

      @if (expandedParking()) {
        <div class="px-6 pb-10 md:px-10">
          <div class="rounded-2xl border border-blue-3 bg-blue-a2 p-6">
            <etxe-property-parking-list
              [form]="form"
              [showLotNumber]="propertyIsCoOwnership()"
            ></etxe-property-parking-list>
          </div>
        </div>
      }
    </div>

    <!-- Common Areas Section -->
    @if (propertyIsCoOwnership()) {
      <div class="rounded-3xl border border-indigo-3 bg-indigo-2 shadow-sm">
        <button
          type="button"
          (click)="toggleSection('commonAreas')"
          class="flex w-full cursor-pointer items-center justify-between p-6 text-left outline-none md:p-10"
        >
          <div class="flex items-center gap-4">
            <div class="flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-5 text-primary">
              <mat-icon fontIcon="groups" class="text-2xl"></mat-icon>
            </div>
            <div>
              <h3 class="text-xl font-bold text-neutral-12">Espace(s) commun(s)</h3>
              <p class="text-sm text-neutral-11">Détails des parties communes de la copropriété</p>
            </div>
          </div>
          <mat-icon>{{ expandedCommonAreas() ? 'expand_less' : 'expand_more' }}</mat-icon>
        </button>

        @if (expandedCommonAreas()) {
          <div class="px-6 pb-10 md:px-10">
            <div class="rounded-2xl border border-indigo-3 bg-indigo-a2 p-6">
              <etxe-property-spaces-list
                title="Liste des espaces communs"
                [formArray]="form.controls.commonAreas"
                itemLabel="Espace"
                addButtonLabel="Ajouter un espace"
                [typeOptions]="commonAreaTypes"
                typeEnumLabel="commonArea"
                descriptionPlaceholder="Ex: Hall, Piscine, Jardin..."
                (add)="addCommonArea()"
                (remove)="removeCommonArea($event)"
              ></etxe-property-spaces-list>
            </div>
          </div>
        }
      </div>
    }

    <!-- Equipments Section -->
    <div class="rounded-3xl border border-neutral-3 bg-neutral-1 shadow-sm">
      <button
        type="button"
        (click)="toggleSection('equipments')"
        class="flex w-full cursor-pointer items-center justify-between p-6 text-left outline-none md:p-10"
      >
        <div class="flex items-center gap-4">
          <div class="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-3 text-primary">
            <mat-icon fontIcon="home_repair_service" class="text-2xl"></mat-icon>
          </div>
          <div>
            <h3 class="text-xl font-bold text-neutral-12">Équipements et confort</h3>
            <p class="text-sm text-neutral-11">Détaillez les prestations qui valorisent votre bien</p>
          </div>
        </div>
        <mat-icon>{{ expandedEquipments() ? 'expand_less' : 'expand_more' }}</mat-icon>
      </button>

      @if (expandedEquipments()) {
        <div class="px-6 pb-10 md:px-10">
          <div class="rounded-2xl border border-neutral-3 bg-neutral-a2 p-6">
            <etxe-property-equipments-list
              [items]="equipmentItems"
              [form]="form.controls.equipments"
            ></etxe-property-equipments-list>
          </div>
        </div>
      }
    </div>
  </div>
</div>
